import React, { useEffect } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import apiClient from "../../axiosClient";
import { useState } from "react";
import DispositionNode from "../../interfaces/dispositionNode";
import DispositionInput from "../../interfaces/disposition_input";
import DropDown from "../baseComponents/dropdown";
import DispositionTreeView from "../baseComponents/dispositionTreeView";
import AddDispositionModal from "../modals/addDispositionModal";
import AddDispositionInputModal from "../modals/addDispositionInputModal";
import JWTTokenPayload from "../../interfaces/JWTTokenPayload";
import { decodedJWT, getOwnerName } from "../../utils/commonUtils";
import ActionConfirmationModal from "../modals/actionConfirmationModal";
import DispositionContext from "../../context/dispositionContext";

const interactionModes = ['Call', 'Visit'];

type DispositionModalProps = {
    parentID: string;
    selectedDispositionNode: DispositionNode | undefined;
};

type DispositionInputModalProps = {
    dispositionID: string;
    selectedDispositionInput: DispositionInput | undefined;
};

type DeleteModalProps = {
    selectedDispositionNode: DispositionNode | undefined;
    selectedDispositionInput: DispositionInput | undefined;
};

const ManageDispositions: React.FC = () => {
    const token: false | JWTTokenPayload = decodedJWT();
    const ownerName = getOwnerName(token);
    const [loading, setLoading] = useState<boolean>(false);
    const [dispositionTree, setDispositionTree] = useState<DispositionNode[]>([]);
    const [filteredDispositionTree, setFilteredDispositionTree] = useState<DispositionNode[]>([]);
    const [openDispositionModal, setOpenDispositionModal] = useState<boolean>(false);
    const [openDispositionInputModal, setOpenDispositionInputModal] = useState<boolean>(false);
    const [dispositionModalProps, setDispositionModalProps] = useState<DispositionModalProps>({
        parentID: "-1", selectedDispositionNode: undefined
    });
    const [dispositionInputModalProps, setDispositionInputModalProps] = useState<DispositionInputModalProps>({
        dispositionID: "-1", selectedDispositionInput: undefined
    });
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [deleteModalProps, setDeleteModalProps] = useState<DeleteModalProps>({
        selectedDispositionInput: undefined, selectedDispositionNode: undefined
    });
    const [interactionMode, setInteractionMode] = useState<string[]>([]);

    const createDispositionTree = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get(`/api/v1/disposition_with_details?client_name=${ownerName || ''}`);
            if (response.status === 200) {
                const tree = DispositionNode.initialise(response.data.dispositions, response.data.disposition_input);
                setDispositionTree(tree);
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const deleteDisposition = async (dispositionID: string) => {
        try {
            setLoading(true);
            const response = await apiClient.delete(`/api/v1/disposition/${dispositionID}`);
            if (response.status === 200) {
                alert('Disposition deleted successfully');
                await createDispositionTree();
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const deleteDispositionInput = async (dispositionInputID: string) => {
        try {
            setLoading(true);
            const response = await apiClient.delete(`/api/v1/disposition_input/${dispositionInputID}`);
            if (response.status === 200) {
                alert('Disposition Input deleted successfully');
                await createDispositionTree();
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDispositionModalOpen = (parentID: string, dispositionNode?: DispositionNode) => {
        setDispositionModalProps({ parentID, selectedDispositionNode: dispositionNode });
        setOpenDispositionModal(true);
    };

    const handleDispositionInputModalOpen = (dispositionID: string, dispositionInput?: DispositionInput) => {
        setDispositionInputModalProps({ dispositionID, selectedDispositionInput: dispositionInput });
        setOpenDispositionInputModal(true);
    };

    const closeDispositionModal = () => {
        setDispositionModalProps({ parentID: "-1", selectedDispositionNode: undefined });
        setOpenDispositionModal(false);
    };

    const closeDispositionInputModal = () => {
        setDispositionInputModalProps({ dispositionID: "-1", selectedDispositionInput: undefined });
        setOpenDispositionInputModal(false);
    };

    const closeDeleteModal = () => {
        setDeleteModalProps({ selectedDispositionInput: undefined, selectedDispositionNode: undefined });
        setOpenDeleteModal(false);
    };

    const deleteModalOpen = (dispositionNode?: DispositionNode, dispositionInput?: DispositionInput) => {
        setDeleteModalProps({ selectedDispositionInput: dispositionInput, selectedDispositionNode: dispositionNode });
        setOpenDeleteModal(true);
    };

    useEffect(() => {
        createDispositionTree();
    }, []);

    useEffect(() => {
        if (dispositionTree.length > 0) {
            let filteredDispositions = dispositionTree.filter(
                val => val.node.is_active && interactionMode.every(value => val.node.interaction_mode.split(',').includes(value))
            );
            setFilteredDispositionTree(filteredDispositions);
        }
    }, [dispositionTree, interactionMode]);

    return (
        <DispositionContext.Provider value={{
            openDeleteModalFunction: deleteModalOpen,
            openDispositionInputModal: handleDispositionInputModalOpen,
            openDispositionModal: handleDispositionModalOpen,
            interactionMode
        }}>
            <Box m={2}>
                {loading ? (
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress color="primary" />
                    </Box>
                ) : (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <DropDown
                                label="Select Interaction Mode"
                                multiple
                                items={interactionModes}
                                selectedValue={interactionMode}
                                onSelection={setInteractionMode}
                                style={{ width: '100%' }}
                            />
                        </Box>
                        <Button variant="outlined" sx={{ mt: 1, mb: 1 }} onClick={() => handleDispositionModalOpen("-1", undefined)}>
                            <Typography>Add Disposition</Typography>
                        </Button>
                        {filteredDispositionTree.map((val: DispositionNode, index: number) => {
                            return (
                                <DispositionTreeView key={index} dispositionNode={val} />
                            );
                        })}
                    </Box>
                )}
                {openDispositionModal && (
                    <AddDispositionModal
                        parentID={dispositionModalProps.parentID}
                        open={openDispositionModal}
                        onClose={closeDispositionModal}
                        getAPIFunction={createDispositionTree}
                        selectedDispositionNode={dispositionModalProps.selectedDispositionNode}
                    />
                )}
                {openDispositionInputModal && (
                    <AddDispositionInputModal
                        open={openDispositionInputModal}
                        onClose={closeDispositionInputModal}
                        getAPIFunction={createDispositionTree}
                        dispositionID={dispositionInputModalProps.dispositionID}
                        selectedDispositionInput={dispositionInputModalProps.selectedDispositionInput}
                    />
                )}
                {openDeleteModal && (
                    <ActionConfirmationModal
                        visible={openDeleteModal}
                        onClose={closeDeleteModal}
                        headerText={`Delete Disposition${
                            deleteModalProps.selectedDispositionNode ? '' : deleteModalProps.selectedDispositionInput ? ' Input' : ''
                        }`}
                        bodyText={`Are you sure you want to delete this Disposition${
                            deleteModalProps.selectedDispositionNode ? ` (${
                                deleteModalProps.selectedDispositionNode.node.disposition_name
                            })` : deleteModalProps.selectedDispositionInput ? ` Input (${
                                deleteModalProps.selectedDispositionInput.field_name
                            })` : ''
                        }?`}
                        onConfirm={deleteModalProps.selectedDispositionNode ?
                            async () => deleteDisposition(deleteModalProps.selectedDispositionNode?.node.disposition_id || '')
                            : deleteModalProps.selectedDispositionInput ?
                                async () => deleteDispositionInput(deleteModalProps.selectedDispositionInput?.disposition_input_id || '')
                                : async () => {}
                        }
                    />
                )}
            </Box>
        </DispositionContext.Provider>
    );
};

export default ManageDispositions;